import { SignKycFormModal } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/SignKycFormModal/SignKycFormModal';
import { InstructionsCardModal } from '@dealstack/frontend/investor/pages/Investment/common/Contracts/Instructions/InstructionsCardModal';
import { PreviewKycFormModal } from '@dealstack/frontend/investor/pages/Investment/common/PreviewKycFormModal/PreviewKycFormModal';
import {
	confirmInvestorKycForm,
	removeInvestorKycForm,
	resetInheritedKycItems,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import {
	nothingToSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetInvestorKycForms } from '@dealstack/frontend/investor/pages/Investment/hooks/useGetInvestorKycForms';
import { InvestorKycFormsTable } from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/InvestorKycFormsTable';
import { InheritedFormsConfirmation } from '@dealstack/frontend/investor/pages/Investment/common/InheritedFormsConfirmation/InheritedFormsConfirmation';
import {
	getInheritedFormsDisplay,
	hasFormAwaitingRequiredSignature,
} from '@dealstack/frontend/investor/pages/Investment/common/InheritedFormsConfirmation/InheritedFormsConfirmation.utils';
import {
	FormPair,
	hasSubmission,
} from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import { EidEasySigningModal } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal';
import { useKycFormSigning } from '@dealstack/frontend/investor/pages/Investment/common/KycFormSigning/useKycFormSigning';

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	isPIVInvestment: boolean;
	kycWorkflowId: string;
}

export const InvestmentKycForms = ({ setIsContinueDisabled, kycWorkflowId }: Props) => {
	const tInvestor = useTranslationInvestor();

	// Forms + their submission state come from the DocumentWorkflow bridges (already scoped to this
	// investment). Each row is a form + its instance status — no KYCFile.
	const formsRequest = useGetInvestorKycForms(kycWorkflowId, { enabled: !!kycWorkflowId });
	const refetch = formsRequest.refetch;

	const pairedForms = useMemo<FormPair[]>(
		() =>
			(formsRequest.data ?? []).map((form) => ({
				templateId: form.templateId,
				instanceId: form.instanceId,
				name: form.formName,
				instruction: form.instruction,
				templateFileId: form.templateFileId,
				fileId: form.fileId,
				fileName: form.fileName,
				status: form.status,
				isInherited: form.isInherited,
				subjectAction: form.subjectAction,
				rejectionComment: form.rejectionComment,
				createdAt: form.createdAt,
				requiresSignature: form.requiresSignature,
				signatureStatus: form.signatureStatus,
				signatureId: form.signatureId,
			})),
		[formsRequest.data]
	);

	const isFormsLoaded = !!formsRequest.data;
	const [inheritedDismissed, setInheritedDismissed] = useState(false);
	const { inheritedForms, isInheritedAndLocked, isConfirmedInherited } = useMemo(
		() => getInheritedFormsDisplay({ forms: pairedForms, inheritedDismissed }),
		[pairedForms, inheritedDismissed]
	);
	const hasRejectedForm = pairedForms.some((f) => isKycRejected(f.status));

	const onConfirmAllForms = useCallback(async () => {
		if (!inheritedForms.length) {
			return;
		}
		await Promise.all(
			inheritedForms.map((f) =>
				confirmInvestorKycForm({ templateId: f.templateId, kycWorkflowId })
			)
		);
		showToastV2({ title: tInvestor('investment.join.kyc.inherited.confirmed') });
		void refetch();
	}, [inheritedForms, kycWorkflowId, refetch, tInvestor]);

	const onDismissInheritedForms = useCallback(async () => {
		const allInherited = pairedForms.filter((f) => f.subjectAction != null);
		if (!allInherited.length) {
			setInheritedDismissed(true);
			return;
		}
		await Promise.all(
			allInherited.map((f) =>
				removeInvestorKycForm({ templateId: f.templateId, kycWorkflowId })
			)
		);
		showToastV2({ title: tInvestor('investment.join.kyc.kyc_forms.removed') });
		setInheritedDismissed(true);
		void refetch();
	}, [pairedForms, kycWorkflowId, refetch, tInvestor]);

	const onResetForms = useCallback(async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.FORM);
		setInheritedDismissed(false);
		void refetch();
	}, [kycWorkflowId, refetch]);

	useEffect(() => {
		if (!pairedForms.length) {
			setIsContinueDisabled(true);
			return;
		}
		if (hasRejectedForm) {
			setIsContinueDisabled(true);
			return;
		}
		if (isConfirmedInherited) {
			setIsContinueDisabled(false);
			return;
		}
		if (isInheritedAndLocked) {
			setIsContinueDisabled(true);
			return;
		}

		const allFilled = pairedForms.every((f) => hasSubmission(f));
		const hasUnconfirmed = pairedForms.some((f) => f.isInherited);
		// A submitted-but-unsigned form is `status: PENDING`, so `hasSubmission` alone would let the
		// investor Continue past it — and the wizard offers no route back.
		const awaitsSignature = hasFormAwaitingRequiredSignature(pairedForms);
		setIsContinueDisabled(!allFilled || hasUnconfirmed || awaitsSignature);
	}, [
		pairedForms,
		hasRejectedForm,
		isInheritedAndLocked,
		isConfirmedInherited,
		setIsContinueDisabled,
	]);

	useWizardStep(nothingToSave);

	const [showWetInkModal, setShowWetInkModal] = useState(false);
	const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
	const [selectedForm, setSelectedForm] = useState<FormPair>();

	const formsWithInstructions = useMemo(
		() => pairedForms.filter((f) => !!f.instruction),
		[pairedForms]
	);
	const [selectedInstructionIndex, setSelectedInstructionIndex] = useState<number>();

	const onDelete = useCallback(
		async (templateId: string) => {
			await removeInvestorKycForm({ templateId, kycWorkflowId });
			showToastV2({ title: tInvestor('investment.join.kyc.kyc_forms.removed') });
			void refetch();
		},
		[kycWorkflowId, refetch, tInvestor]
	);

	const onPreview = useCallback((form: FormPair) => {
		setShowDocumentPreviewModal(true);
		setSelectedForm(form);
	}, []);

	const onSign = useCallback((form: FormPair) => {
		setShowWetInkModal(true);
		setSelectedForm(form);
	}, []);

	const { startSigningForm, signingModalProps } = useKycFormSigning({
		onSigned: refetch,
		kycWorkflowId,
	});

	const onInstructionClick = useCallback(
		(templateId: string) => {
			const index = formsWithInstructions.findIndex((f) => f.templateId === templateId);
			setSelectedInstructionIndex(index);
		},
		[formsWithInstructions]
	);

	const selectedInstructionForm =
		selectedInstructionIndex != null ? formsWithInstructions[selectedInstructionIndex] : null;

	if (!isFormsLoaded) {
		return null;
	}

	return (
		<div>
			{isInheritedAndLocked || isConfirmedInherited ? (
				<InheritedFormsConfirmation
					inheritedForms={isConfirmedInherited ? pairedForms : inheritedForms}
					onConfirm={onConfirmAllForms}
					onDismiss={onDismissInheritedForms}
					onReset={onResetForms}
					confirmed={isConfirmedInherited}
				/>
			) : (
				<>
					{!!pairedForms.length && (
						<InvestorKycFormsTable
							data={pairedForms}
							onDelete={onDelete}
							onPreview={onPreview}
							onSign={onSign}
							onInstructionClick={onInstructionClick}
							onStartSigning={startSigningForm}
						/>
					)}
				</>
			)}

			{/*
				Page-level sibling, exactly as the contract flow mounts it: the post-signing refetch
				re-derives the table's columns (and, for an inherited set, swaps the table out for
				InheritedFormsConfirmation), which would unmount a cell-mounted modal mid
				success-screen. Mantine's Modal renders nothing while `isModalOpen` is false.
			*/}
			<EidEasySigningModal {...signingModalProps} />

			{showWetInkModal && !!selectedForm && (
				<SignKycFormModal
					onClose={() => {
						setShowWetInkModal(false);
						setSelectedForm(undefined);
					}}
					opened={showWetInkModal && !!selectedForm}
					form={selectedForm}
					refreshData={refetch}
					kycWorkflowId={kycWorkflowId}
				/>
			)}

			{showDocumentPreviewModal && !!selectedForm && (
				<PreviewKycFormModal
					opened={showDocumentPreviewModal && !!selectedForm}
					onClose={() => {
						setShowDocumentPreviewModal(false);
						setSelectedForm(undefined);
					}}
					form={selectedForm}
				/>
			)}

			{selectedInstructionIndex != null && !!selectedInstructionForm && (
				<InstructionsCardModal
					instruction={selectedInstructionForm.instruction}
					title={selectedInstructionForm.name}
					onClose={() => setSelectedInstructionIndex(undefined)}
					onNext={() => setSelectedInstructionIndex(selectedInstructionIndex + 1)}
					onPrev={() => setSelectedInstructionIndex(selectedInstructionIndex - 1)}
					disableNext={selectedInstructionIndex + 1 > formsWithInstructions.length - 1}
					disablePrev={selectedInstructionIndex - 1 < 0}
				/>
			)}
		</div>
	);
};
